import type { AbodeAggregateMarketConditions } from '@aloft/toolkit-shared';
import { Icon } from '@aloft/toolkit-sui';

import { useEffect, useState } from 'react';

import { LoadingScreenSam } from '../../../components/LoadingScreen/LoadingScreenSam.tsx';
import { Flex } from '../../../summary/components/Flex.tsx';
import { AbsorptionRate } from './graphs/AbsorptionRate.tsx';
import { MedianSalePriceToListPrice } from './graphs/MediaSalePricetoListPrice.tsx';
import { MedianDOM } from './graphs/MedianDOM.tsx';
import { MedianListPrice } from './graphs/MedianListPrice.tsx';
import { MedianSalePrice } from './graphs/MedianSalePrice.tsx';
import { MonthsOfHousingSupply } from './graphs/MonthsOfHousingSupply.tsx';
import { TotalComparableListings } from './graphs/TotalComparableListings.tsx';
import { TotalComparableSales } from './graphs/TotalComparableSales.tsx';

const CHART_ANIMATION_DURATION_MS = 1500;

export const MarketConditionsGraphs = ({
  marketAggregateData,
  isLoading,
  isError,
}: {
  marketAggregateData: AbodeAggregateMarketConditions;
  isLoading: boolean;
  isError: boolean;
}) => {
  const [disableAnimations, setDisableAnimations] = useState(false);
  const dataKey = marketAggregateData?.monthlyData.map((item) => item.month).join(',') ?? '';

  useEffect(() => {
    if (isLoading || isError || !dataKey) return;

    setDisableAnimations(false);
    const timeout = window.setTimeout(
      () => setDisableAnimations(true),
      CHART_ANIMATION_DURATION_MS,
    );
    return () => window.clearTimeout(timeout);
  }, [isLoading, isError, dataKey]);

  if (isLoading) {
    return <LoadingScreenSam overrideText="Loading market conditions..." />;
  }
  if (isError || !marketAggregateData || marketAggregateData.monthlyData.length === 0) {
    return (
      <Flex alignItems="center" gap={2}>
        <Icon icon="error-warning" color="textError" /> Error loading market condition analysis
        data. Please try another filter or ZIP code
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" gap={6} marginBottom={6}>
      <TotalComparableSales data={marketAggregateData} disableAnimations={disableAnimations} />
      <AbsorptionRate data={marketAggregateData} disableAnimations={disableAnimations} />
      <TotalComparableListings data={marketAggregateData} disableAnimations={disableAnimations} />
      <MonthsOfHousingSupply data={marketAggregateData} disableAnimations={disableAnimations} />
      <MedianSalePrice data={marketAggregateData} disableAnimations={disableAnimations} />
      <MedianListPrice data={marketAggregateData} disableAnimations={disableAnimations} />
      <MedianSalePriceToListPrice
        data={marketAggregateData}
        disableAnimations={disableAnimations}
      />
      <MedianDOM data={marketAggregateData} disableAnimations={disableAnimations} />
    </Flex>
  );
};
